<script setup lang="ts">
/**
 * UI-kit component
 * Category: Headers
 * Name: HeaderButton
 * FIGMA: https://www.figma.com/design/NgI2cVJkWODrgnOBHO3Zed/Strapi-Builder--DS-?node-id=327-26323
 *
 * A top-level nav item. view=dropdown shows a chevron that flips to chevron-up
 * while the panel is open (the host opens the panel on hover/focus); when it also
 * has a url it renders as a navigable link, otherwise as a button. view=link
 * navigates directly, chevron hidden.
 * Desktop bar: hug width, Title/S, py space/20. Inside the tablet/mobile sheet:
 * full-width row, Title/M, label and chevron pushed to opposite ends.
 * No disable state by design; the browser focus outline is the ONLY keyboard
 * focus indicator — never suppress it.
 * Local implementation of the Figma DS component missing from @brighterly/lib-lumee-ui.
 */
// types
import type { HeaderButtonProps } from '../types/interfaces'
// hooks
import { useHeaderButton } from '../composables/use-header-button.hook'
// components
import { Icon } from '@brighterly/lib-lumee-ui'

defineOptions({
  name: 'HeaderButton',
})

const props = withDefaults(defineProps<HeaderButtonProps>(), {
  view: 'dropdown',
  url: '',
  open: false,
})

const emit = defineEmits<{
  click: []
}>()

const {
  refIsDropdown,
  refIsLink,
  refChevronName,
  handleClick,
} = useHeaderButton({
  props,
  onClick: () => emit('click'),
})
</script>

<template>
  <a
    v-if="refIsLink"
    class="header-button"
    :class="{ 'header-button--open': open }"
    :href="url"
    :aria-expanded="refIsDropdown ? (open ? 'true' : 'false') : undefined"
    @click="handleClick"
  >
    <span class="header-button__label btly-text-title-m lg:btly-text-title-s">{{ label }}</span>
    <span
      v-if="refIsDropdown"
      class="header-button__chevron"
    >
      <Icon
        :name="refChevronName"
        size="sm"
        weight="solid"
        color="inherit"
      />
    </span>
  </a>
  <button
    v-else
    type="button"
    class="header-button"
    :class="{ 'header-button--open': open }"
    :aria-expanded="open ? 'true' : 'false'"
    @click="handleClick"
  >
    <span class="header-button__label btly-text-title-m lg:btly-text-title-s">{{ label }}</span>
    <span class="header-button__chevron">
      <Icon
        :name="refChevronName"
        size="sm"
        weight="solid"
        color="inherit"
      />
    </span>
  </button>
</template>

<style lang="scss" scoped src="../styles/_header-button.scss" />
